import { useEffect, useState } from "react";
import { BellRing, X } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../i18n";
import { api } from "../../lib/api";
import { currentSubscription, enablePush, needsHomeScreen, pushSupported } from "../../lib/push";
import { IosGuide } from "./NotifySettings";
import { btnGhost, btnPrimary } from "./Page";

const KEY = "vavlis.pushPrompt";
const SNOOZE_DAYS = 14;

const snoozed = () => Number(localStorage.getItem(KEY) || 0) > Date.now();

// Friendly explanation shown a few seconds after login (never on the first page load of the login screen).
export const PushPrompt = ({ staff }) => {
  const { t } = useI18n();
  const [cfg, setCfg] = useState(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (snoozed()) return undefined;
    const id = setTimeout(() => {
      api.get("/notifications/config").then(async (r) => {
        const c = r.data;
        const ios = needsHomeScreen();
        const here = await currentSubscription().catch(() => null);
        const ask = c.push_available && !here && (ios || (pushSupported() && Notification.permission === "default"));
        setCfg(c); setShow(Boolean(ask));
      }).catch(() => {});
    }, 4000);
    return () => clearTimeout(id);
  }, []);
  if (!show) return null;
  const later = () => { localStorage.setItem(KEY, String(Date.now() + SNOOZE_DAYS * 86400000)); setShow(false); };
  const enable = async () => {
    try {
      const res = await enablePush(cfg.public_key);
      if (res === "granted") toast.success(t("x.push.enabled")); else toast.error(t("x.push.denied"));
    } catch (e) { toast.error(t("common.error")); }
    later();
  };
  return (
    <div className={`fixed inset-x-3 bottom-24 z-40 mx-auto max-w-md rounded-2xl bg-white p-4 shadow-2xl ring-1 ring-slate-200 animate-in slide-in-from-bottom-4 duration-300 ${staff ? "lg:bottom-6" : ""}`} data-testid="push-prompt">
      <button type="button" onClick={later} aria-label="close" className="absolute right-2 top-2 grid h-9 w-9 place-items-center rounded-full text-slate-400 hover:bg-slate-100" data-testid="push-prompt-close"><X className="h-4 w-4" /></button>
      <div className="flex gap-3">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-brand/20 text-navy"><BellRing className="h-5 w-5" /></span>
        <div className="min-w-0 pr-6">
          <p className="font-semibold text-navy">{t("x.push.promptTitle")}</p>
          <p className="mt-1 text-sm text-slate-600">{t(staff ? "x.push.promptStaff" : "x.push.promptBody")}</p>
        </div>
      </div>
      {needsHomeScreen() ? <div className="mt-3"><IosGuide /></div> : (
        <div className="mt-3 flex gap-2">
          <button type="button" onClick={later} className={`${btnGhost} flex-1`} data-testid="push-prompt-later">{t("x.push.notNow")}</button>
          <button type="button" onClick={enable} className={`${btnPrimary} flex-1`} data-testid="push-prompt-enable">{t("x.push.enable")}</button>
        </div>
      )}
    </div>
  );
};
